/**
 * RNR's Switch, on the restyle's squared construction — 46×28 at 5px, a 22px knob at 4px.
 *
 * Tuxedo's construction, grafted into Bouquet along with the rest of the harder corners, so both
 * looks draw the same object and only the two colours differ: Bouquet's on-track is the app's red,
 * Tuxedo's is the slate it uses in BOTH modes — the one surface that carries a white knob at
 * 9.79:1 rather than 1.66:1 on a grey one. `switch-on`, `track` and `knob` carry all of it, so
 * nothing here knows which room it is in.
 *
 * The primitive is what makes it a switch rather than a Pressable that looks like one: it owns
 * `role="switch"`, the checked state, the disabled state, and — on the web — the keyboard, which a
 * hand-built Pressable had no way to grow.
 *
 * The knob is absolutely positioned rather than flex-aligned, so its travel is two numbers rather
 * than a justify-content flip: 3px in from whichever end it is resting against.
 */
import { cn } from '@/lib/utils';
import * as SwitchPrimitive from '@rn-primitives/switch';
import { Platform } from 'react-native';

function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        'h-7 w-[46px] rounded-[5px] border',
        /**
         * OFF CARRIES ITS OWN EDGE. `--track` against `--card` is 1.75:1 in the Bouquet dark room
         * and 1.12:1 in the Tuxedo one — an off switch that is the card it is sitting on, which is
         * how the readiness capture read the mute row as already muted. On is a fill and needs no
         * line, so it keeps the border's WIDTH and loses only its colour: taking the border away
         * would move the knob's 3px inset by a pixel between the two states.
         */
        props.checked ? 'bg-switch-on border-transparent' : 'bg-track border-line-strong',
        props.disabled && 'opacity-50',
        Platform.select({ web: 'focus-visible:ring-ring/50 cursor-default focus-visible:ring-[3px] disabled:pointer-events-none' }),
        className,
      )}
      {...props}>
      <SwitchPrimitive.Thumb
        className={cn('bg-knob absolute top-[3px] size-[22px] rounded-[4px]', props.checked ? 'left-[21px]' : 'left-[3px]')}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
